import type { LucideIcon } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import type { AdminTheme } from '@/context/AdminThemeContext';
import { cn } from '@/utils/cn';
import { adminShell } from '@/utils/adminTheme';

export interface AdminMetricCardProps {
  title: string;
  value: string;
  subtitleLines?: string[];
  icon: LucideIcon;
  iconBg: string;
  theme: AdminTheme;
  valueSize?: 'md' | 'lg';
}

export function AdminMetricCard({
  title,
  value,
  subtitleLines,
  icon: Icon,
  iconBg,
  theme,
  valueSize = 'lg',
}: AdminMetricCardProps) {
  const shell = adminShell(theme);

  return (
    <Card className={cn('relative overflow-hidden p-5', shell.card)}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <p className={cn('text-[11px] font-semibold uppercase tracking-wider', shell.cardMuted)}>{title}</p>
        <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white shadow-sm', iconBg)}>
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
      </div>

      <p
        className={cn(
          'font-bold tabular-nums leading-snug',
          valueSize === 'md' ? 'text-lg' : 'text-2xl',
        )}
      >
        {value}
      </p>

      {subtitleLines && subtitleLines.length > 0 && (
        <div className="mt-3 space-y-1">
          {subtitleLines.map((line) => (
            <p key={line} className={cn('text-xs leading-snug', shell.cardMuted)}>
              {line}
            </p>
          ))}
        </div>
      )}

      <Icon
        className={cn('pointer-events-none absolute -bottom-3 -right-3 h-20 w-20 opacity-[0.06]', iconBg.replace('bg-', 'text-'))}
        aria-hidden
        strokeWidth={1.25}
      />
    </Card>
  );
}
